import { useId, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { login, type LoginFailure } from "../api/http";
import { Banner } from "../components/ui/ErrorState";
import { Button } from "../components/ui/Button";

/**
 * Acceso con la contraseña única de la aplicación (`APP_PASSWORD`, sección 3.2).
 *
 * QUÉ HACE
 *   `POST /api/auth/login` con `{ password }` (sección 9.1). El servidor responde `204` y deja la
 *   cookie firmada; `401` si la contraseña no es la buena; `429` a partir del sexto intento en un
 *   minuto, que es el limitador de M4-T01.
 *
 * DÓNDE VA CADA ERROR
 *   El `401` se pinta **junto al campo**, porque depende de lo que acabas de escribir. El `429`
 *   es un aviso de pantalla, porque ya no depende de eso: hay que esperar. Mezclarlos haría que
 *   el usuario siguiera probando contraseñas cuando el problema es otro. Quién es cada final lo
 *   decide `login()` en `api/http.ts`; esta pantalla solo decide **dónde** se enseña.
 *
 * ÚNICA PANTALLA SIN PESTAÑAS
 *   Sin sesión no hay a dónde ir. La ruta cuelga del router por su cuenta, fuera de `AppShell`.
 */

type LoginError = LoginFailure | null;

export function Login() {
  const navigate = useNavigate();
  const campoId = useId();
  const errorId = useId();
  const campo = useRef<HTMLInputElement>(null);

  const [password, setPassword] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<LoginError>(null);

  async function alEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (enviando) return;

    setEnviando(true);
    setError(null);

    try {
      const resultado = await login(password);

      if (resultado.ok) {
        navigate("/", { replace: true });
        return;
      }

      setError(resultado.reason);
      if (resultado.reason === "credentials") {
        setPassword("");
        campo.current?.focus();
      }
    } finally {
      setEnviando(false);
    }
  }

  const errorDeCampo =
    error === "credentials"
      ? "La contraseña no es correcta."
      : error === "network"
        ? "No se ha podido conectar con la aplicación."
        : null;

  return (
    <main className="mx-auto grid min-h-screen max-w-content content-start gap-3 px-3.5 pb-10 pt-14">
      <h1 className="grid justify-items-center gap-1 text-center">
        <span className="font-display text-[40px] font-semibold leading-none text-pitch">
          Biwenger
        </span>
        <span className="font-display text-[22px] font-semibold leading-none tracking-[0.14em] text-muted">
          Manager
        </span>
      </h1>

      <form
        onSubmit={alEnviar}
        className="grid gap-3.5 rounded-xl border border-border bg-surface p-3"
      >
        <div className="grid gap-1.5">
          <label
            htmlFor={campoId}
            className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted"
          >
            Contraseña
          </label>
          <input
            ref={campo}
            id={campoId}
            type="password"
            name="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(evento) => setPassword(evento.target.value)}
            aria-invalid={errorDeCampo !== null}
            aria-describedby={errorDeCampo ? errorId : undefined}
            // 16 px de letra: por debajo, iOS hace zoom al enfocar el campo y descuadra la pantalla.
            className={`min-h-touch rounded-[9px] border bg-white/5 px-2.5 text-base text-ink placeholder:text-muted ${
              errorDeCampo ? "border-down" : "border-border"
            }`}
          />
          {errorDeCampo ? (
            <span id={errorId} role="alert" className="text-xs text-down">
              {errorDeCampo}
            </span>
          ) : null}
        </div>

        <Button type="submit" variant="primary" block disabled={enviando}>
          {enviando ? "Entrando…" : "Entrar"}
        </Button>
      </form>

      {error === "rate-limit" ? (
        <Banner tone="error" title="Demasiados intentos">
          Has fallado varias veces seguidas. Espera un momento y vuelve a
          intentarlo.
        </Banner>
      ) : null}

      <p className="text-center text-xs text-muted">
        Aplicación personal. Un solo usuario.
      </p>
    </main>
  );
}
